import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Pin, Archive, ArchiveRestore, ArrowRightLeft, Send, CalendarClock, ExternalLink, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/common/StatusBadge";
import { FounderAvatar } from "./FounderAvatar";
import { MiniMarkdown } from "./MiniMarkdown";
import { catTone, statusBadgeTone } from "@/data/notebookOptions";
import { relatedLink } from "@/data/relatedRecords";
import { NoteReactions } from "./NoteReactions";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

export const NoteDetail = ({ note, open, onOpenChange, canEditThis, canWrite, myName, highlightCommentId, onEdit, onDelete, onPin, onArchive, onConvert, onComment, onReact }) => {
  const [comment, setComment] = useState("");
  const [mentions, setMentions] = useState([]);
  const highlightRef = useRef(null);
  useEffect(() => {
    if (open && highlightCommentId && highlightRef.current) {
      const t = setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
      return () => clearTimeout(t);
    }
  }, [open, highlightCommentId, note]);
  if (!note) return null;
  const tone = catTone(note.category);
  const toggleMention = (f) => setMentions((m) => m.includes(f) ? m.filter((x) => x !== f) : [...m, f]);

  const submitComment = async () => {
    if (!comment.trim()) return;
    await onComment(note.id, comment.trim(), mentions);
    setComment(""); setMentions([]);
    toast.success("Comment added");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="note-detail" className="max-h-[92vh] max-w-2xl overflow-y-auto border-[#E7E2D4] bg-[#FFFDF7]">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white" style={{ backgroundColor: tone }}>{note.category}</span>
            <StatusBadge status={statusBadgeTone(note.status)} label={note.status} />
            {note.visibility !== "Both founders" && <StatusBadge status="warning" label={note.visibility} />}
            {note.pinned && <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#E0A82E]"><Pin size={12} fill="#E0A82E" /> Pinned</span>}
          </div>
          <DialogTitle className="mt-1 font-heading text-2xl text-[#0D1B2A]">{note.title}</DialogTitle>
          <DialogDescription className="flex items-center gap-2 text-[#8A8674]">
            <FounderAvatar name={note.author} size={22} /> {note.author} · {fmt(note.created_at)}
            {note.review_date && <span className="inline-flex items-center gap-1 text-[#C2410C]"><CalendarClock size={13} /> Review {note.review_date}</span>}
          </DialogDescription>
        </DialogHeader>

        {note.body && <div className="rounded-xl border border-[#E7E2D4] bg-white p-4"><MiniMarkdown text={note.body} /></div>}

        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-[#8A8674]">Reactions</span>
          <NoteReactions note={note} canReact={canWrite && (myName === "Steven" || myName === "Richard")} myName={myName} onReact={onReact} />
        </div>

        {(note.tags?.length || 0) > 0 && (
          <div className="flex flex-wrap gap-1.5">{note.tags.map((t) => <span key={t} className="rounded-full bg-[#0EA5A4]/10 px-2 py-0.5 text-[11px] font-semibold text-[#0C8E8D]">#{t}</span>)}</div>
        )}

        {(note.related_records?.length || 0) > 0 && (
          <div>
            <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-[#8A8674]">Linked records</p>
            <div className="flex flex-wrap gap-1.5">
              {note.related_records.map((r) => (
                <Link key={`${r.type}-${r.id}`} to={relatedLink(r)} data-testid={`note-related-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#1D4ED8]/10 px-2.5 py-1 text-xs font-semibold text-[#1D4ED8] hover:bg-[#1D4ED8]/20">
                  {r.type}: {r.name} <ExternalLink size={11} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {(note.converted_to?.length || 0) > 0 && (
          <div data-testid="note-converted-panel" className="rounded-xl border border-[#E0A82E]/30 bg-[#FBF6E9] p-3">
            <p className="mb-1.5 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#8A6314]"><ArrowRightLeft size={12} /> Converted into</p>
            <ul className="space-y-1 text-sm text-[#5C6B7B]">{note.converted_to.map((c, i) => (
              <li key={`${c.at || ""}-${i}`}>
                {c.target}: {c.record_id ? (
                  <Link to={relatedLink({ type: c.target, id: c.record_id, name: c.record_name })} data-testid={`note-converted-link-${c.record_id}`} className="inline-flex items-center gap-1 font-semibold text-[#1D4ED8] hover:underline">{c.record_name} <ExternalLink size={11} /></Link>
                ) : <span className="font-semibold text-[#0D1B2A]">{c.record_name}</span>}
                <span className="text-[11px] text-[#8A8674]"> · by {c.by} {fmt(c.at)}</span>
              </li>
            ))}</ul>
          </div>
        )}

        {/* Comments */}
        <div className="border-t border-[#E7E2D4] pt-3">
          <p className="mb-2 font-heading text-sm font-semibold text-[#0D1B2A]">Comments</p>
          {(note.comments?.length || 0) === 0 ? <p className="text-sm text-[#8A8674]">No comments yet.</p> : (
            <ul className="space-y-2" data-testid="note-comments">
              {note.comments.map((c) => (
                <li key={c.id} data-testid={`note-comment-${c.id}`} ref={highlightCommentId === c.id ? highlightRef : null}
                  className={`rounded-xl border bg-white p-3 transition-all ${highlightCommentId === c.id ? "border-[#E0A82E] ring-2 ring-[#E0A82E]/40" : "border-[#E7E2D4]"}`}>
                  <div className="mb-1 flex items-center justify-between"><span className="flex items-center gap-1.5 text-sm font-semibold text-[#0D1B2A]"><FounderAvatar name={c.author} size={18} /> {c.author}</span><span className="text-[11px] text-[#8A8674]">{fmt(c.at)}</span></div>
                  <MiniMarkdown text={c.text} />
                  {(c.mentions?.length || 0) > 0 && <div className="mt-1 flex gap-1">{c.mentions.map((m) => <span key={m} className="rounded bg-[#E0A82E]/15 px-1.5 text-[11px] font-semibold text-[#8A6314]">@{m}</span>)}</div>}
                </li>
              ))}
            </ul>
          )}
          {canWrite && (
            <div className="mt-2 space-y-2">
              <Input data-testid="note-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment… use @Steven / @Richard to flag" className="border-[#E7E2D4] bg-white" onKeyDown={(e) => { if (e.key === "Enter") submitComment(); }} />
              <div className="flex items-center gap-2">
                <button data-testid="note-mention-steven" onClick={() => toggleMention("Steven")} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${mentions.includes("Steven") ? "bg-[#1D4ED8] text-white" : "bg-[#1D4ED8]/10 text-[#1D4ED8]"}`}>@Steven</button>
                <button data-testid="note-mention-richard" onClick={() => toggleMention("Richard")} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${mentions.includes("Richard") ? "bg-[#0EA5A4] text-white" : "bg-[#0EA5A4]/10 text-[#0C8E8D]"}`}>@Richard</button>
                <Button size="sm" data-testid="note-comment-submit" onClick={submitComment} className="ml-auto rounded-full bg-[#0D1B2A] text-white hover:bg-[#1B2A3B]"><Send size={13} /> Comment</Button>
              </div>
            </div>
          )}
        </div>

        {/* History */}
        {(note.history?.length || 0) > 0 && (
          <details className="border-t border-[#E7E2D4] pt-3">
            <summary className="flex cursor-pointer items-center gap-1.5 text-sm font-semibold text-[#5C6B7B]"><History size={14} /> Note history ({note.history.length})</summary>
            <ul className="mt-2 space-y-1 text-xs text-[#8A8674]" data-testid="note-history">
              {note.history.slice().reverse().map((h, i) => <li key={`${h.at || ""}-${i}`}>· {h.detail} <span className="text-[#B7B19C]">— {h.actor}, {fmt(h.at)}</span></li>)}
            </ul>
          </details>
        )}

        {/* Actions */}
        {canWrite && (
          <div className="flex flex-wrap gap-2 border-t border-[#E7E2D4] pt-3">
            <Button data-testid="note-convert-btn" onClick={() => onConvert(note)} className="rounded-full bg-[#E0A82E] text-white hover:bg-[#C4901F]"><ArrowRightLeft size={15} /> Convert</Button>
            <Button variant="outline" data-testid="note-pin-toggle" onClick={() => onPin(note)} className="border-[#E7E2D4]"><Pin size={15} /> {note.pinned ? "Unpin" : "Pin"}</Button>
            {canEditThis && <Button variant="outline" data-testid="note-edit" onClick={() => onEdit(note)} className="border-[#E7E2D4]"><Pencil size={15} /> Edit</Button>}
            {canEditThis && <Button variant="outline" data-testid="note-archive" onClick={() => onArchive(note)} className="border-[#E7E2D4]">{note.status === "Archived" ? <><ArchiveRestore size={15} /> Restore</> : <><Archive size={15} /> Archive</>}</Button>}
            {canEditThis && <Button variant="outline" data-testid="note-delete" onClick={() => onDelete(note)} className="border-[#E7E2D4] text-[#DC2626] hover:bg-[#DC2626]/10"><Trash2 size={15} /> Delete</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
